import axios from 'axios';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

function normalizeIds(obj: unknown): unknown {
  if (Array.isArray(obj)) return obj.map(normalizeIds);
  if (obj && typeof obj === 'object') {
    const o = obj as Record<string, unknown>;
    if (o['_id'] !== undefined && o['id'] === undefined) o['id'] = o['_id'];
    delete o['_id'];
    delete o['__v'];
    for (const key of Object.keys(o)) o[key] = normalizeIds(o[key]);
  }
  return obj;
}

// Public routes that must remain reachable without auth. A 401 from the
// AuthProvider's background /auth/me check (stale localStorage) must NOT bounce
// these to /login — otherwise e.g. a supplier following a WhatsApp share link
// can't open the page. We still clear the stale user, just don't redirect.
const PUBLIC_PATH_PREFIXES = [
  '/supplier/',
  '/verify/',
  '/portal/',
  '/gallery',
  '/colors',
  '/sign/',
];

function isOnPublicPath(): boolean {
  const path = window.location.pathname;
  return PUBLIC_PATH_PREFIXES.some((p) => path === p || path.startsWith(p));
}

// A 401 normally means the current SESSION is dead — clear the stored user
// and (outside public pages) bounce to /login. But a 401 from an auth ATTEMPT
// endpoint speaks only about the credential being presented (wrong password,
// dead magic link), not the session: an expired/revoked magic link opened by
// an already signed-in user must not wipe their valid session.
const AUTH_ATTEMPT_ENDPOINTS = ['/auth/login', '/auth/portal-login', '/auth/portal-link-exchange'];

export function isAuthAttemptUrl(url: string | undefined): boolean {
  if (!url) return false;
  return AUTH_ATTEMPT_ENDPOINTS.some((e) => url === e || url.endsWith(e));
}

apiClient.interceptors.response.use(
  (response) => {
    response.data = normalizeIds(response.data);
    return response;
  },
  (error) => {
    if (error.response?.status === 401 && !isAuthAttemptUrl(error.config?.url)) {
      localStorage.removeItem('authUser');
      if (!isOnPublicPath()) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;
